import {ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon} from "lucide-react";
import {useState} from "react";

import {Badge} from "@components/ui/badge";
import {VNextChecksSummary} from "@hooks/use-vnext";

import {displayCheckLabel} from "./check-labels";
import {getRemediation} from "./check-remediation";

interface ChecksPanelProps {
    checksSummary: VNextChecksSummary | null | undefined;
    checksScope?: "full_model";
}

export function ChecksPanel({checksSummary, checksScope}: ChecksPanelProps) {
    const [expanded, setExpanded] = useState(false);

    if (!checksSummary || checksSummary.results.length === 0) return null;

    const {passed, warned, failed, results} = checksSummary;
    const overallStatus = failed > 0 ? "FAIL" : warned > 0 ? "WARN" : "PASS";

    return (
        <div
            className={`rounded-md border text-xs ${
                overallStatus === "FAIL"
                    ? "border-red-200 bg-red-50/50"
                    : overallStatus === "WARN"
                      ? "border-amber-200 bg-amber-50/50"
                      : "border-green-200 bg-green-50/50"
            }`}
        >
            <div
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer select-none"
                onClick={() => setExpanded(!expanded)}
            >
                <ShieldCheckIcon
                    className={`size-3.5 ${
                        overallStatus === "FAIL"
                            ? "text-red-600"
                            : overallStatus === "WARN"
                              ? "text-amber-600"
                              : "text-green-600"
                    }`}
                />
                <span className="font-medium">Data Quality Checks</span>
                {checksScope === "full_model" && (
                    <span className="text-muted-foreground text-[10px]">(full model)</span>
                )}
                <div className="flex items-center gap-1.5">
                    {passed > 0 && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-green-100 text-green-800">
                            {passed} pass
                        </Badge>
                    )}
                    {warned > 0 && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700">
                            {warned} warn
                        </Badge>
                    )}
                    {failed > 0 && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                            {failed} fail
                        </Badge>
                    )}
                </div>
                <div className="ml-auto">
                    {expanded ? <ChevronUpIcon className="size-3.5" /> : <ChevronDownIcon className="size-3.5" />}
                </div>
            </div>

            {expanded && (
                <div className="border-t px-3 py-2 space-y-1.5">
                    {results.map((result, i) => {
                        const isImmaterial = result.status === "WARN" && Math.abs(result.delta) < 100;
                        const remediation = result.status !== "PASS" ? getRemediation(result.check_id) : null;
                        return (
                            <div
                                key={`${result.check_id}-${result.period}-${i}`}
                                className={`flex items-start gap-2 ${isImmaterial ? "opacity-60" : ""}`}
                            >
                                <Badge
                                    variant={
                                        result.status === "PASS"
                                            ? "secondary"
                                            : result.status === "WARN"
                                              ? "outline"
                                              : "destructive"
                                    }
                                    className={`text-[10px] px-1.5 py-0 mt-0.5 shrink-0 ${
                                        result.status === "PASS"
                                            ? "bg-green-100 text-green-800"
                                            : result.status === "WARN"
                                              ? "border-amber-300 text-amber-700"
                                              : ""
                                    }`}
                                >
                                    {result.status}
                                </Badge>
                                <div className="min-w-0">
                                    <span className="text-[11px]">{displayCheckLabel(result.check_id)}</span>
                                    {result.period !== "all" && (
                                        <span className="text-muted-foreground ml-1">({result.period})</span>
                                    )}
                                    <p className="text-muted-foreground truncate">{result.message}</p>
                                    {result.status !== "PASS" && result.delta !== 0 && (
                                        <p className="text-muted-foreground">
                                            Actual: {result.actual.toLocaleString()} | Expected: {result.expected.toLocaleString()} | Delta: {result.delta.toLocaleString()}
                                            {result.delta_pct != null && ` (${(result.delta_pct * 100).toFixed(2)}%)`}
                                        </p>
                                    )}
                                    {remediation && (
                                        <div className="mt-1 rounded border border-amber-200 bg-amber-50/30 p-2 text-[10px] space-y-0.5">
                                            <p><span className="font-medium">What happened:</span> {remediation.whatHappened}</p>
                                            <p><span className="font-medium">What to do:</span> {remediation.whatToDo}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
